import { ky } from './ky';
import { ListResponse } from './common';

/**
 * 판매 국가 정책. 백엔드 `SalesCountryPolicyDto`와 매칭된다.
 */
export interface SalesCountryPolicyDto {
  countryCode: string;
  countryName: string;
  defaultCurrencyCode: string;
  salesEnabled: boolean;
  shippingEnabled: boolean;
  policyVersion: number;
  effectiveFrom: string;
  effectiveTo?: string | null;
}

/**
 * 통화 정책. 백엔드 `CurrencyPolicyDto`와 매칭된다.
 */
export interface CurrencyPolicyDto {
  currencyCode: string;
  displayName: string;
  symbol: string;
  fractionDigits: number;
  enabled: boolean;
  effectiveFrom: string;
  effectiveTo?: string | null;
}

/**
 * 주소 입력 필드 정의. 백엔드 `AddressFieldDto`와 매칭된다.
 */
export interface AddressFieldDto {
  name: string;
  label: string;
  required: boolean;
  maxLength: number;
}

/**
 * 국가별 주소 스키마. 백엔드 `AddressSchemaDto`와 매칭된다.
 */
export interface AddressSchemaDto {
  countryCode: string;
  defaultCurrencyCode: string;
  policyVersion: number;
  fields: AddressFieldDto[];
  requiredFields: string[];
  effectiveFrom: string;
  effectiveTo?: string | null;
}

/**
 * 세금 정책. 백엔드 `TaxPolicyDto`와 매칭된다.
 */
export interface TaxPolicyDto {
  countryCode: string;
  taxType: string;
  displayName: string;
  rateBasisPoints: number;
  includedInPrice: boolean;
  policyVersion: number;
  effectiveFrom: string;
  effectiveTo?: string | null;
}

/**
 * 배송 정책. 백엔드 `ShippingPolicyDto`와 매칭된다.
 */
export interface ShippingPolicyDto {
  countryCode: string;
  productId?: number | null;
  shippable: boolean;
  unavailableReasonCode?: string | null;
  baseShippingFeeAmount: number;
  freeShippingThresholdAmount?: number | null;
  remoteAreaAdditionalFeeAmount: number;
  currencyCode: string;
  summary: string;
  tax?: TaxPolicyDto | null;
  policyVersion: number;
  effectiveFrom: string;
  effectiveTo?: string | null;
}

/**
 * 소비자(취소/반품/교환) 정책. 백엔드 `ConsumerPolicyDto`와 매칭된다.
 */
export interface ConsumerPolicyDto {
  countryCode: string;
  cancellationWindowDays: number;
  returnWindowDays: number;
  exchangeWindowDays: number;
  policyNotice: string;
  policyVersion: number;
  effectiveFrom: string;
  effectiveTo?: string | null;
}

/**
 * 커머스 국가/통화/배송/반품 정책 API 클라이언트.
 * 백엔드 `CommercePolicyController`(/v1/commerce/policies)와 매칭된다.
 * 상품 상세 페이지(lone-frontend#8)에서 배송/반품 정책 표시에 사용된다.
 */
export default class CommercePolicyService {
  /**
   * 판매 국가 목록 조회
   */
  async listCountries() {
    const response = await ky.get('v1/commerce/policies/countries');
    return (await response.json()) as ListResponse<SalesCountryPolicyDto>;
  }

  /**
   * 통화 목록 조회
   */
  async listCurrencies() {
    const response = await ky.get('v1/commerce/policies/currencies');
    return (await response.json()) as ListResponse<CurrencyPolicyDto>;
  }

  /**
   * 국가별 주소 입력 스키마 조회
   *
   * @param countryCode 배송 국가 코드
   */
  async getAddressSchema(countryCode: string) {
    const response = await ky.get('v1/commerce/policies/address-schema', {
      searchParams: { countryCode },
    });
    return (await response.json()) as AddressSchemaDto;
  }

  /**
   * 배송 정책 조회
   *
   * @param countryCode 판매 국가 코드
   * @param productId 상품 ID (선택, 상품별 정책 우선 적용)
   */
  async getShippingPolicy(countryCode: string, productId?: number) {
    const searchParams: Record<string, string> = { countryCode };
    if (productId != null) searchParams.productId = String(productId);

    const response = await ky.get('v1/commerce/policies/shipping', { searchParams });
    return (await response.json()) as ShippingPolicyDto;
  }

  /**
   * 반품/교환 정책 조회
   *
   * @param countryCode 판매 국가 코드
   */
  async getReturnPolicy(countryCode: string) {
    const response = await ky.get('v1/commerce/policies/returns', {
      searchParams: { countryCode },
    });
    return (await response.json()) as ConsumerPolicyDto;
  }
}
